"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useDispatch } from "react-redux";
import Cookies from "js-cookie";
import {
  ArrowLeft,
  Lock,
  Phone,
  Eye,
  EyeOff,
  RefreshCw,
  ChevronDown,
  Mail,
  CheckCircle2,
  X,
} from "lucide-react";
import {
  mockLogin,
  checkIdentifierExists,
  ROLE_DASHBOARD_MAP,
} from "@/lib/mockAuth";
import { setCredentials } from "@/store/slices/authSlice";

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const COUNTRY_CODES = [
  { code: "+91", label: "India" },
  { code: "+1", label: "USA" },
  { code: "+44", label: "UK" },
  { code: "+971", label: "UAE" },
  { code: "+65", label: "Singapore" },
];

const inputClass =
  "w-full border border-border rounded-lg px-3.5 py-3 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors";

function Toast({ message, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className="fixed top-6 right-6 z-50 flex items-center gap-2.5 bg-foreground text-background rounded-lg px-4 py-3 shadow-xl animate-in fade-in slide-in-from-top-2">
      <CheckCircle2 size={16} className="text-success shrink-0" />
      <span className="text-sm">{message}</span>
      <button
        onClick={onClose}
        className="text-background/60 hover:text-background ml-1"
      >
        <X size={14} />
      </button>
    </div>
  );
}

// step: "start" -> "password" | "mobile" -> "otp" | "forgot"
export default function LoginPage() {
  const [step, setStep] = useState("start");
  const [identifier, setIdentifier] = useState("");
  const [checkingIdentifier, setCheckingIdentifier] = useState(false);

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const [countryCode, setCountryCode] = useState("+91");
  const [countryOpen, setCountryOpen] = useState(false);
  const [phone, setPhone] = useState("");

  const [otpDigits, setOtpDigits] = useState(Array(6).fill(""));
  const otpRefs = useRef([]);
  const [captcha, setCaptcha] = useState("");
  const [captchaCode, setCaptchaCode] = useState(() =>
    Math.floor(10000 + Math.random() * 90000).toString(),
  );
  const [resendTimer, setResendTimer] = useState(30);
  const [toast, setToast] = useState("");

  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useDispatch();
  const router = useRouter();

  useEffect(() => {
    if (step !== "otp") return;
    if (resendTimer <= 0) return;
    const t = setInterval(() => setResendTimer((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [step, resendTimer]);

  const goToDashboard = (user, token) => {
    Cookies.set("aeronpay_token", token, { expires: rememberMe ? 30 : 7 });
    Cookies.set("aeronpay_role", user.role, { expires: rememberMe ? 30 : 7 });
    dispatch(setCredentials(user));
    router.push(ROLE_DASHBOARD_MAP[user.role] || "/login");
  };

  const formatPhoneDisplay = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 10);
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  };

  const handleContinueWithEmail = async (e) => {
    e.preventDefault();
    if (!identifier) return;
    setError("");
    setCheckingIdentifier(true);
    const exists = await checkIdentifierExists(identifier);
    setCheckingIdentifier(false);
    if (!exists) {
      setError("We couldn't find an account with that email or username.");
      return;
    }
    setStep("password");
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);
    try {
      const { user, token } = await mockLogin(identifier, password);
      goToDashboard(user, token);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendOtp = (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10) {
      setError("Enter a valid 10-digit mobile number");
      return;
    }
    setError("");
    setResendTimer(30);
    setStep("otp");
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otpDigits];
    next[index] = value;
    setOtpDigits(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleResendOtp = () => {
    if (resendTimer > 0) return;
    setResendTimer(30);
    setCaptchaCode(Math.floor(10000 + Math.random() * 90000).toString());
    setOtpDigits(Array(6).fill(""));
    setToast("OTP resent successfully");
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (captcha !== captchaCode) {
      setError("Captcha doesn't match");
      return;
    }
    if (otpDigits.some((d) => !d)) {
      setError("Enter the complete 6-digit OTP");
      return;
    }
    setIsSubmitting(true);
    try {
      const { user, token } = await mockLogin(
        "employee@aeronpay.in",
        "password123",
      );
      goToDashboard(user, token);
    } catch (err) {
      setError("Invalid OTP");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);
    const exists = await checkIdentifierExists(forgotEmail);
    setIsSubmitting(false);
    if (!exists) {
      setError("We couldn't find an account with that email.");
      return;
    }
    setForgotSent(true);
  };

  const handleGoogleLogin = () => {
    setError("Google login will be available once backend OAuth is connected.");
  };

  const goBack = () => {
    setError("");
    if (step === "otp") setStep("mobile");
    else if (step === "forgot") {
      setStep("password");
      setForgotSent(false);
    } else setStep("start");
  };

  return (
    <div>
      {toast && <Toast message={toast} onClose={() => setToast("")} />}

      {step !== "start" && (
        <button
          onClick={goBack}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-6 transition-colors hover:cursor-pointer"
        >
          <ArrowLeft size={14} /> Back
        </button>
      )}

      {/* STEP: start */}
      {step === "start" && (
        <>
          <h1 className="text-5xl text-center font-display leading-[1.1] text-foreground mb-3 font-medium">
            Where Work <span className="italic font-medium">Meets People.</span>
          </h1>
          <p className="text-muted-foreground text-center mb-8 text-[15px]">
            Connect your people, processes, and workplace in one place.
          </p>

          <div className="border border-border rounded-2xl p-6 space-y-3">
            <form onSubmit={handleContinueWithEmail} className="space-y-3">
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  type="text"
                  required
                  autoFocus
                  placeholder="Enter your username or email"
                  value={identifier}
                  onChange={(e) => {
                    setIdentifier(e.target.value);
                    setError("");
                  }}
                  className={inputClass + " pl-10"}
                />
              </div>
              {error && <p className="text-xs text-destructive">{error}</p>}
              <button
                type="submit"
                disabled={checkingIdentifier}
                className="w-full bg-foreground text-background rounded-lg py-3 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 hover:cursor-pointer"
              >
                {checkingIdentifier ? "Checking..." : "Continue"}
              </button>
            </form>
            <div className="flex items-center gap-3 py-1">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs text-muted-foreground uppercase tracking-wide">
                or
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <button
              onClick={() => {
                setStep("mobile");
                setError("");
              }}
              className="w-full flex items-center justify-center gap-2.5 border border-border rounded-lg py-3 mt-4 text-sm font-medium text-foreground hover:bg-muted transition-colors hover:cursor-pointer"
            >
              <Phone size={16} /> Login with Mobile
            </button>
          </div>

          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-2.5 border border-border rounded-full py-3 mt-4 text-sm font-medium text-foreground hover:bg-muted transition-colors hover:cursor-pointer"
          >
            <GoogleIcon /> Continue with Google
          </button>
        </>
      )}

      {/* STEP: password */}
      {step === "password" && (
        <>
          <h1 className="text-2xl font-semibold text-foreground mb-1">
            Enter password
          </h1>
          <p className="text-sm text-muted-foreground mb-8">
            Continue as{" "}
            <span className="text-foreground font-medium">{identifier}</span>
          </p>

          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div className="relative">
              <Lock
                size={16}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type={showPassword ? "text" : "password"}
                required
                autoFocus
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass + " pl-10 pr-10"}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:cursor-pointer"
              >
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>

            <p className="text-xs text-muted-foreground -mt-1">
              Try: password123
            </p>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-border accent-primary cursor-pointer"
                />
                Remember me
              </label>
              <button
                type="button"
                onClick={() => {
                  setStep("forgot");
                  setForgotEmail(identifier);
                  setError("");
                }}
                className="text-xs text-primary hover:underline hover:cursor-pointer"
              >
                Forgot password?
              </button>
            </div>

            {error && <p className="text-xs text-destructive">{error}</p>}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-foreground text-background rounded-lg py-3 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 hover:cursor-pointer"
            >
              {isSubmitting ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </>
      )}

      {/* STEP: forgot password */}
      {step === "forgot" && (
        <>
          {!forgotSent ? (
            <>
              <h1 className="text-2xl font-semibold text-foreground mb-1">
                Reset password
              </h1>
              <p className="text-sm text-muted-foreground mb-8">
                Enter your email and we'll send you a link to reset your
                password.
              </p>
              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <div className="relative">
                  <Mail
                    size={16}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <input
                    type="email"
                    required
                    autoFocus
                    placeholder="you@company.com"
                    value={forgotEmail}
                    onChange={(e) => {
                      setForgotEmail(e.target.value);
                      setError("");
                    }}
                    className={inputClass + " pl-10"}
                  />
                </div>
                {error && <p className="text-xs text-destructive">{error}</p>}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-foreground text-background rounded-lg py-3 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 hover:cursor-pointer"
                >
                  {isSubmitting ? "Sending..." : "Send reset link"}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-6">
              <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={22} className="text-success" />
              </div>
              <h2 className="text-lg font-semibold text-foreground mb-1.5">
                Check your email
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                We sent a password reset link to{" "}
                <span className="text-foreground font-medium">
                  {forgotEmail}
                </span>
              </p>
              <button
                onClick={() => {
                  setStep("start");
                  setForgotSent(false);
                }}
                className="text-sm text-primary hover:underline font-medium hover:cursor-pointer"
              >
                Back to sign in
              </button>
            </div>
          )}
        </>
      )}

      {/* STEP: mobile number */}
      {step === "mobile" && (
        <>
          <h1 className="text-2xl font-semibold text-foreground mb-1">
            Login with mobile
          </h1>
          <p className="text-sm text-muted-foreground mb-8">
            We'll send a one-time code to verify
          </p>

          <form onSubmit={handleSendOtp} className="space-y-4">
            <div className="flex gap-3">
              <div className="relative shrink-0">
                <button
                  type="button"
                  onClick={() => setCountryOpen(!countryOpen)}
                  className="flex items-center gap-1.5 border border-border rounded-lg px-3.5 py-3 text-sm text-foreground bg-background hover:bg-muted/50 transition-colors hover:cursor-pointer"
                >
                  {countryCode}{" "}
                  <ChevronDown size={13} className="text-muted-foreground" />
                </button>
                {countryOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setCountryOpen(false)}
                    />
                    <div className="absolute top-full left-0 mt-1.5 w-40 bg-card border border-border rounded-lg shadow-lg py-1.5 z-20">
                      {COUNTRY_CODES.map((c) => (
                        <button
                          key={c.code}
                          type="button"
                          onClick={() => {
                            setCountryCode(c.code);
                            setCountryOpen(false);
                          }}
                          className={`w-full flex items-center justify-between px-3 py-2 text-xs transition-colors ${countryCode === c.code ? "text-primary bg-primary/5" : "text-foreground hover:bg-muted hover:cursor-pointer"}`}
                        >
                          <span>{c.label}</span>
                          <span className="text-muted-foreground">
                            {c.code}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <input
                type="tel"
                required
                autoFocus
                placeholder="98765-43210"
                value={phone}
                onChange={(e) => setPhone(formatPhoneDisplay(e.target.value))}
                className="flex-1 border border-border rounded-lg px-3.5 py-3 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors tracking-wide"
              />
            </div>

            {error && <p className="text-xs text-destructive">{error}</p>}

            <button
              type="submit"
              className="w-full bg-foreground text-background rounded-lg py-3 text-sm font-semibold hover:opacity-90 transition-opacity hover:cursor-pointer"
            >
              Send OTP
            </button>
          </form>
        </>
      )}

      {/* STEP: OTP + captcha — redesigned */}
      {step === "otp" && (
        <>
          <h1 className="text-2xl font-semibold text-foreground mb-1">
            Enter OTP
          </h1>
          <p className="text-sm text-muted-foreground mb-8">
            Code sent to {countryCode} {phone}
          </p>

          <form onSubmit={handleOtpSubmit} className="space-y-6">
            <div className="flex items-center justify-between gap-2">
              {otpDigits.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (otpRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  className="w-11 h-13 text-center text-lg font-semibold border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  style={{ height: "52px" }}
                />
              ))}
            </div>

            <div className="bg-muted/40 border border-border rounded-xl p-4 space-y-3">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                Verify you're human
              </p>
              <div className="flex items-center gap-3">
                <div className="flex-1 flex items-center justify-center py-2.5 rounded-lg bg-background border border-border relative overflow-hidden select-none">
                  <span
                    className="text-base font-bold text-foreground"
                    style={{
                      letterSpacing: "0.4em",
                      fontStyle: "italic",
                      background:
                        "linear-gradient(90deg, var(--color-foreground), var(--color-muted-foreground))",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {captchaCode}
                  </span>
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(45deg, transparent, transparent 6px, var(--color-border) 6px, var(--color-border) 7px)",
                      opacity: 0.3,
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setCaptchaCode(
                      Math.floor(10000 + Math.random() * 90000).toString(),
                    )
                  }
                  className="w-11 h-11 shrink-0 border border-border rounded-lg flex items-center justify-center hover:bg-muted transition-colors bg-background hover:cursor-pointer"
                >
                  <RefreshCw size={15} className="text-muted-foreground" />
                </button>
              </div>
              <input
                type="text"
                required
                placeholder="Enter the code above"
                value={captcha}
                onChange={(e) => setCaptcha(e.target.value)}
                className={inputClass}
              />
            </div>

            {error && <p className="text-xs text-destructive">{error}</p>}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-foreground text-background rounded-lg py-3 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 hover:cursor-pointer"
            >
              {isSubmitting ? "Verifying..." : "Verify & Sign in"}
            </button>

            <p className="text-center text-xs text-muted-foreground">
              Didn't receive it?{" "}
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={resendTimer > 0}
                className={`font-medium ${resendTimer > 0 ? "text-muted-foreground cursor-not-allowed" : "text-primary hover:underline hover:cursor-pointer"}`}
              >
                {resendTimer > 0
                  ? `Resend OTP in ${resendTimer}s`
                  : "Resend OTP"}
              </button>
            </p>
          </form>
        </>
      )}

      <p className="text-center text-xs text-muted-foreground mt-10">
        By signing in, you agree to our{" "}
        <a href="/terms" className="text-primary hover:underline">
          Terms
        </a>{" "}
        and{" "}
        <a href="/privacy" className="text-primary hover:underline">
          Privacy Policy
        </a>
      </p>
    </div>
  );
}
